import { Icon } from '@iconify/react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { NotificationStatus, RecurrenceType, type Notification } from '../../../types/ApiTypes'
import type { NotificationPayload } from '../../components/SendPushModal'
import SendPushModal from '../../components/SendPushModal'
import ConfirmActionModal from '../../components/ConfirmActionModal'
import Dashboard from '../../layout/Dashboard'
import { notificationService } from '../../services/NotificationService'
import { ListHead, ListRow, NavContainer, NavWrapper } from '../../styles/CrypoAssetStyle'
import { Content, Heading } from '../../styles/OverviewStyle'

export default function Notifications(): React.JSX.Element {
   const [query, setQuery] = useState('')
   const [statusFilter, setStatusFilter] = useState<NotificationStatus | ''>('')
   const [sendOpen, setSendOpen] = useState(false)
   const [notifications, setNotifications] = useState<Notification[]>([])
   const [loading, setLoading] = useState(false)
   const [editingNotification, setEditingNotification] = useState<Notification | null>(null)
   const [confirmCancel, setConfirmCancel] = useState<string | null>(null)
   const [confirmResend, setConfirmResend] = useState<string | null>(null)
   const navigate = useNavigate()

   const fetchNotifications = useCallback(async () => {
      setLoading(true)
      try {
         const filters: { search: string; status?: NotificationStatus } = {
            search: query
         }
         if (statusFilter) filters.status = statusFilter

         const response = await notificationService.getNotifications(filters)
         if (response.status === 'success' && response.data) {
            setNotifications(response.data);
         }
      } catch (error) {
         console.error('Failed to fetch notifications:', error)
      } finally {
         setLoading(false)
      }
   }, [query, statusFilter])

   useEffect(() => {
      fetchNotifications()
   }, [fetchNotifications])


   async function handleSendNotification(payload: NotificationPayload) {
      try {
         await notificationService.sendNotification(payload)
         fetchNotifications()
      } catch (error) {
         console.error('Failed to send notification:', error)
      }
   }

   async function handleCancelNotification() {
      if (!confirmCancel) return
      try {
         await notificationService.cancelNotification(confirmCancel)
         setConfirmCancel(null)
         fetchNotifications()
      } catch (error) {
         console.error('Failed to cancel notification:', error)
      }
   }

   async function handleResendNotification() {
      if (!confirmResend) return
      try {
         await notificationService.resendNotification(confirmResend)
         setConfirmResend(null)
         fetchNotifications()
      } catch (error) {
         console.error('Failed to resend notification:', error)
      }
   }

   async function handleUpdateNotification(payload: NotificationPayload) {
      if (!editingNotification) return
      try {
         await notificationService.updateNotification(editingNotification.id, payload)
         setEditingNotification(null)
         fetchNotifications()
      } catch (error) {
         console.error('Failed to update notification:', error)
      }
   }

   function formatRecurrence(notification: Notification): string | undefined {
      if (!notification.isRecurring) return undefined
      if (notification.recurrenceType === RecurrenceType.DAILY) return 'Daily'
      if (notification.recurrenceType === RecurrenceType.WEEKLY && notification.dayOfWeek !== null) {
         const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
         return `Every ${days[notification.dayOfWeek]}`
      }
      if (notification.recurrenceType === RecurrenceType.MONTHLY && notification.dayOfMonth !== null) {
         return `Every ${notification.dayOfMonth}th`
      }
      return notification.recurrenceType || undefined
   }

   function formatDateTime(dateStr: string | null): string {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true })
   }

   const activeNotifications = notifications.filter(n => n.status === NotificationStatus.ACTIVE || n.status === NotificationStatus.PENDING)
   const previousNotifications = notifications.filter(n => n.status === NotificationStatus.SENT || n.status === NotificationStatus.STOPPED)

   return (
      <>
         <Dashboard title="Notification / Push notification" icon="solar:bell-linear" active="notifications">
            <Content>
               <div className="flex items-center justify-between mb-4">
                  <Heading>Notification</Heading>
                  <button onClick={() => setSendOpen(true)} className="px-4 py-2 rounded-full bg-[#AF7AD5] text-white text-sm font-semibold tracking-tight hover:bg-[#9b63c9] cursor-pointer inline-flex items-center gap-1.5">
                     <Icon icon="gg:alarm" width={16} />
                     Send Push
                  </button>
               </div>

               <NavContainer>
                  <NavWrapper children="Push Notification" $active />
                  <NavWrapper children="Activity Log" onClick={() => navigate('/notifications/activity-log')} />
               </NavContainer>

               <div className="flex items-center gap-2 mt-6 mb-4">
                  <div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
                     <Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
                     <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search"
                        className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
                  </div>

                  <select
                     value={statusFilter}
                     onChange={e => setStatusFilter(e.target.value as NotificationStatus | '')}
                     className="flex items-center gap-1.5 px-3 py-1.5 h-[28px] text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors"
                  >
                     <option value="">All Status</option>
                     <option value={NotificationStatus.ACTIVE}>Active</option>
                     <option value={NotificationStatus.PENDING}>Pending</option>
                     <option value={NotificationStatus.SENT}>Sent</option>
                     <option value={NotificationStatus.STOPPED}>Stopped</option>
                  </select>
               </div>

               {loading ? (
                  <div className="flex justify-center py-8">
                     <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#AF7AD5]" />
                  </div>
               ) : (
                  <>
                     {activeNotifications.length > 0 && (
                        <div>
                           <ListHead style={{ gridTemplateColumns: '1fr 180px 200px' }}>
                              <div className="text-[12px] tracking-tight text-[#9C9C9F] font-geist-mono">Active</div>
                              <div className='text-right pr-2'></div>
                              <div></div>
                           </ListHead>
                           {activeNotifications.map(n => (
                              <ListRow key={n.id} style={{ gridTemplateColumns: '1fr 180px 200px' }}>
                                 <div>
                                    <div className="text-sm text-[#101018] font-medium gap-2">
                                       <span className='mr-3'>{n.title}</span>
                                       {formatRecurrence(n) && <span className="text-[10px] bg-[#F5F9FF] rounded-full px-2 py-1 font-geist-mono text-[#1B52B8] inline-flex items-center gap-1">
                                          <Icon icon="gg:alarm" width={12} /> {formatRecurrence(n)}
                                       </span>}
                                    </div>
                                    <div className="text-xs text-[#B3B3B6] mt-1 text-left">{n.body}</div>
                                 </div>
                                 <div className="text-left text-xs text-[#B3B3B6]">{formatDateTime(n.scheduledAt || n.lastSentAt)}</div>
                                 <div className="flex items-center justify-end gap-2">
                                    <button onClick={() => setConfirmCancel(n.id)} className="px-3 py-1.5 rounded-md font-medium text-xs border border-[#E7E7E9] text-[#101018] hover:bg-[#F9F9FA] cursor-pointer inline-flex items-center gap-1">
                                       <Icon icon='material-symbols:close' width={13} /> Cancel
                                    </button>
                                    <button onClick={() => { setEditingNotification(n); setSendOpen(true) }} className="px-3 py-1.5 rounded-md font-medium text-xs border border-[#E7E7E9] text-[#101018] hover:bg-[#F9F9FA] cursor-pointer inline-flex items-center gap-1">
                                       <Icon icon="solar:pen-2-linear" width={14} /> Edit
                                    </button>
                                 </div>
                              </ListRow>
                           ))}
                        </div>
                     )}

                     {previousNotifications.length > 0 && (
                        <div>
                           <ListHead style={{ gridTemplateColumns: '1fr 180px 200px' }}>
                              <div className="text-[12px] tracking-tight text-[#9C9C9F] mt-6 font-geist-mono">Previous</div>
                              <div className='text-right pr-2'></div>
                              <div></div>
                           </ListHead>
                           {previousNotifications.map(n => (
                              <ListRow key={n.id} style={{ gridTemplateColumns: '1fr 180px 200px' }}>
                                 <div>
                                    <div className="text-sm text-[#101018] font-medium">{n.title}</div>
                                    <div className="text-xs text-[#6D6D72] mt-1 truncate">{n.body}</div>
                                 </div>
                                 <div className="text-left text-xs text-[#9C9C9F]">{formatDateTime(n.lastSentAt)}</div>
                                 <div className="flex items-center justify-end">
                                    <button onClick={() => setConfirmResend(n.id)} className="px-3 py-1.5 rounded-md font-medium text-xs border border-[#E7E7E9] text-[#101018] hover:bg-[#F9F9FA] cursor-pointer inline-flex items-center gap-1">
                                       <Icon icon="tabler:reload" width={12} /> Resend
                                    </button>
                                 </div>
                              </ListRow>
                           ))}
                        </div>
                     )}

                     {activeNotifications.length === 0 && previousNotifications.length === 0 && (
                        <div className="text-center py-8 text-[#9C9C9F]">No notifications found</div>
                     )}
                  </>
               )}
            </Content>
         </Dashboard>
         <SendPushModal
            open={sendOpen}
            onClose={() => { setSendOpen(false); setEditingNotification(null) }}
            onSend={editingNotification ? handleUpdateNotification : handleSendNotification}
         />
         <ConfirmActionModal
            open={!!confirmCancel}
            title="Cancel Notification"
            message="Are you sure you want to cancel this notification? This action cannot be undone."
            confirmText="Cancel Notification"
            onClose={() => setConfirmCancel(null)}
            onConfirm={handleCancelNotification}
         />
         <ConfirmActionModal
            open={!!confirmResend}
            title="Resend Notification"
            message="Are you sure you want to resend this notification to all users?"
            confirmText="Resend"
            onClose={() => setConfirmResend(null)}
            onConfirm={handleResendNotification}
         />
      </>
   )
}
